import { DatePicker, Input, Popover, notification } from 'antd';
import { ChangeEvent, Fragment, PureComponent, ReactNode, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { VitalsEntity } from '../../../../../store/types/prescription';
import { SERVER_VITALS, UnitsWithRangesEntity, VITALS_AS_DATE } from '../../../leftPanel/vitals';
import AntSelect from './AntSelect';

import {
    bmiVitalIcon,
    crossIcon,
    dropVitalIcon,
    gestatinalAgeIcon,
    heartVitalIcon,
    heightVitalIcon,
    infoIcon2,
    lungsVitalIcon,
    ofcIcon,
    temperatureVitalIcon,
    weightVitalIcon,
} from '../../../../common/Icons';
import { GVitals } from '../../../../../store/db';
import { getGLocal } from '../../../../../store/idbHandler';
import { DB_TABLES } from '../../../../../store/dbTables';
import { VITALS } from '../../../../../constants';
import { VitalsConfig } from '../../../../../api/pushGenie';
import { getVitalsPadConfig } from '../../../../../store/selectors/session';
import { GlobalState } from '../../../../../store/reducers';
import { isLocalItem } from '../../../../../pages/utils';
import { upsertVitalsConfig } from '../../../../../store/actions/session';
import { track } from '../../../../../tracking';
import { AntTooltip } from '../../../../common/AntTooltip';
import { getMyDbId } from '../../../../../api/mactiveid';
import moment from 'moment';

export const WEIGHT_ID = 'lb-7991006736';
export const HEIGHT_ID = 'lb-7561569471';
export const BMI_ID = 'lb-5305097546';
export const LBS_ID = 'vu-2307875107';
export const CMS_ID = 'vu-2305998272';
export const FEET_ID = 'vu-3019850218';

type Props = {
    elements: ReadonlyArray<VitalsEntity>;
    containerId?: string;
    vitalConfigData?: VitalsConfig;
    gVitalData: GVitals[];
    onElementUpdate: (option: VitalsEntity, index?: number) => void;
    onElementDelete: (id: string, index?: number) => void;
};

type State = {
    vitalConfigElement: VitalsEntity[];
    isLoadedFromIdb: boolean;
};

type VitalsEntityWithStatus = VitalsEntity & { status?: boolean };

export class VitalTable extends PureComponent<Props, State> {
    UNITS_MAP: { [key: string]: UnitsWithRangesEntity[] | undefined | null } = {};
    state: State = {
        vitalConfigElement:
            this.props.vitalConfigData?.selectedItems?.map((t) => ({
                id: t.id,
                name: t.name,
                dis_name: t.name,
                value: { qt: '', unit: '' },
            })) || [],
        isLoadedFromIdb: false,
    };

    setVitalsData = async (): Promise<void> => {
        const mdbVitals = VITALS;
        const { vitalConfigData, gVitalData } = this.props;

        if (vitalConfigData?.selectedItems === undefined) {
            return;
        }

        mdbVitals.forEach((v) => {
            this.UNITS_MAP[v.id] = v.units_with_ranges;
        });

        const mdbVitalDbFormatData =
            mdbVitals.map((v) => ({
                ...v,
                dis_name: v.dis_name,
                value: {
                    qt: '',
                    unit: (this.UNITS_MAP[v.id] as UnitsWithRangesEntity[])?.[0].dis_unit,
                },
            })) || [];

        const gVitals =
            gVitalData?.map((v) => ({
                ...v,
                dis_name: v.name,
                value: {
                    qt: '',
                    unit: v.unit,
                },
            })) || [];

        const data =
            (vitalConfigData.selectedItems
                .map((cEle) => {
                    return [...mdbVitalDbFormatData, ...gVitals].find((ele) => ele.id === cEle.id);
                })
                .filter((ele) => !!ele) as VitalsEntity[]) || [];

        this.setState({ vitalConfigElement: data, isLoadedFromIdb: true });
    };

    componentDidMount = (): void => {
        this.setVitalsData();
    };

    componentDidUpdate = (prevProps: Props): void => {
        if (
            JSON.stringify(prevProps.vitalConfigData?.selectedItems) !==
                JSON.stringify(this.props.vitalConfigData?.selectedItems) ||
            JSON.stringify(prevProps.gVitalData) !== JSON.stringify(this.props.gVitalData)
        ) {
            this.setVitalsData();
        }
    };

    fillBMI = ({
        weight,
        height,
        weightUnit,
        heightUnit,
    }: {
        weight?: string;
        height?: string;
        weightUnit?: string;
        heightUnit?: string;
    }): void => {
        const { vitalConfigData } = this.props;

        if (!vitalConfigData?.selectedItems.find((t) => t.id === BMI_ID)) {
            return;
        }

        let weightValue;
        let heightValue;
        let BMI;

        const { elements, onElementUpdate } = this.props;

        const weightVital = elements.find((v) => v.id === WEIGHT_ID);

        const heightVital = elements.find((v) => v.id === HEIGHT_ID);

        const bmiVital = SERVER_VITALS.find((v) => v.id === BMI_ID);

        const weightVitalUnitId =
            weightUnit ||
            (
                this.UNITS_MAP[weightVital?.id || ''] &&
                (this.UNITS_MAP[weightVital?.id || ''] as UnitsWithRangesEntity[])
            )?.find((u) => u.dis_unit === weightVital?.value.unit || '')?.code_id ||
            '';

        if (weightVitalUnitId === LBS_ID) {
            weightValue = this.lbsToKgs(
                parseFloat(weight || weightVital?.value.qt || ''),
            ).toString();
        } else {
            weightValue = weight || weightVital?.value.qt;
        }

        // Height Vital should be from MDB to check unit (cms/feet)

        const heightVitalUnitId =
            heightUnit ||
            (
                this.UNITS_MAP[heightVital?.id || ''] &&
                (this.UNITS_MAP[heightVital?.id || ''] as UnitsWithRangesEntity[])
            )?.find((u) => u.dis_unit === heightVital?.value.unit || '')?.code_id ||
            '';

        if (heightVitalUnitId === CMS_ID) {
            heightValue = this.cmsToMeter(
                parseFloat(height || heightVital?.value.qt || ''),
            ).toString();
        }
        if (heightVitalUnitId === FEET_ID) {
            const heightInFeet = height || heightVital?.value.qt;
            heightValue = this.feetToMeter(this.parseHeightInFeet(heightInFeet || '')).toString();
        }

        if (
            weightValue &&
            !isNaN(parseFloat(weightValue)) &&
            heightValue &&
            !isNaN(parseFloat(heightValue))
        ) {
            BMI = parseFloat(weightValue) / (parseFloat(heightValue) * parseFloat(heightValue));
            const isUpdate = elements.filter((v) => v.id === BMI_ID).length > 0 ? true : false;

            onElementUpdate(
                {
                    name: bmiVital?.name || '',
                    dis_name: bmiVital?.dis_name || '',
                    id: bmiVital?.id || '',
                    value: {
                        qt: BMI.toFixed(1) || '',
                        unit: (this.UNITS_MAP[bmiVital?.id || ''] as UnitsWithRangesEntity[])[0]
                            .dis_unit,
                        safe: (this.UNITS_MAP[bmiVital?.id || ''] as UnitsWithRangesEntity[])[0]
                            .safe,
                    },
                    interpretation: undefined,
                    isoDate: new Date().toISOString(),
                    remark: undefined,
                },
                isUpdate ? elements.findIndex((v) => v.id === BMI_ID) : elements.length,
            );
        }
    };

    lbsToKgs = (weightInLbs: number): number => {
        return weightInLbs * 0.453592;
    };

    cmsToMeter = (cms: number): number => {
        return cms * 0.01;
    };

    feetToMeter = (feet: number): number => {
        return feet * 0.3048;
    };

    parseHeightInFeet = (height: string): number => {
        const heightInFeet = height.split("'");
        return (
            parseFloat(String(heightInFeet[0])) + (parseFloat(String(heightInFeet[1])) / 12 || 0)
        );
    };

    updateElement = (option: VitalsEntity, id: string): void => {
        const { elements, onElementUpdate, onElementDelete } = this.props;
        const index = elements.findIndex((el) => el.id === id);
        if (option.value.qt) {
            onElementUpdate(option, index === -1 ? undefined : index);
        } else {
            onElementDelete(option.id);
        }
        if (option.id === HEIGHT_ID || option.id === WEIGHT_ID) {
            setTimeout(() => {
                this.fillBMI({});
            }, 500);
        }
    };

    findElementUnits = (el: VitalsEntity): UnitsWithRangesEntityAndId[] | undefined => {
        const units =
            this.UNITS_MAP[el.id] && this.UNITS_MAP[el.id]?.map((v) => ({ ...v, id: v.code_id }));
        return (
            units ||
            (el.value.unit
                ? [
                      {
                          code: el.value.code || '',
                          code_id: el.value.code_id || '',
                          dis_unit: el.value.unit,
                          id: el.value.code_id || '',
                          safe: { low: '', high: '', type: 'string' },
                          system: '',
                      },
                  ]
                : undefined)
        );
    };

    findDefaultUnit = (el: VitalsEntity): UnitsWithRangesEntityAndId | undefined => {
        const units = this.findElementUnits(el);
        const currentVitalDefaultOptionId = this.props.vitalConfigData?.selectedItems.find(
            (i) => i.id === el.id,
        )?.defaultOptionId;

        return (
            (el.value.qt
                ? units?.find((v) => v.dis_unit === el.value.unit)
                : units?.find((i) => i.code_id === currentVitalDefaultOptionId)) || units?.[0]
        );
    };

    onChange = (option: VitalsEntity, inp: string): void => {
        if (option.id === HEIGHT_ID)
            this.fillBMI({
                height: inp,
                heightUnit: option.value.code_id,
            });
        else if (option.id === WEIGHT_ID)
            this.fillBMI({
                weight: inp,
                weightUnit: option.value.code_id,
            });
    };

    render(): ReactNode {
        const { vitalConfigElement, isLoadedFromIdb } = this.state;
        const { elements, vitalConfigData } = this.props;

        const activeElements = vitalConfigElement.map((ele) => {
            const isElementPresent = elements.find((activeEle) => activeEle.id === ele.id);
            return isElementPresent ? { ...isElementPresent, status: true } : ele;
        });

        return activeElements && activeElements.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 w-full">
                {activeElements?.map((el, i) => {
                    return (
                        <VitalsCell
                            key={`${el.id}${i}${el.locked}${isLoadedFromIdb}`}
                            el={el}
                            index={i}
                            updateElement={this.updateElement}
                            units={this.findElementUnits(el)}
                            defaultUnit={this.findDefaultUnit(el)}
                            isLoadedFromIdb={isLoadedFromIdb}
                            vitalConfig={vitalConfigData}
                            onChange={this.onChange}
                            lockStatus={!!el.locked}
                        />
                    );
                })}
            </div>
        ) : (
            <p className="text-darwin-neutral-600 font-400 text-14 select-none ">
                No quick options to show. please “Configure ” your Vitals.
            </p>
        );
    }
}

export const vitalToIconMapping = (id: string): JSX.Element => {
    switch (id) {
        case 'lb-1201285132':
            return heartVitalIcon();
        case 'lb-1771358750':
            return lungsVitalIcon();
        case 'v-1365277675':
            return dropVitalIcon();
        case 'lb-8415690160':
            return lungsVitalIcon();
        case 'lb-199711567':
            return temperatureVitalIcon();
        case 'lb-7561569471':
            return heightVitalIcon();
        case 'lb-7991006736':
        case 'lb-3436660046':
            return weightVitalIcon();
        case 'lb-5305097546':
            return bmiVitalIcon();
        case 'lb-9242267093':
            return dropVitalIcon();
        case 'lb-8296133834':
            return dropVitalIcon();
        case 'lb-7205446246':
            return ofcIcon();
        case 'lb-5257276261':
            return gestatinalAgeIcon();
        default:
            return temperatureVitalIcon();
    }
};

interface UnitsWithRangesEntityAndId extends UnitsWithRangesEntity {
    id: string;
}

const VitalsCell: React.FC<{
    el: VitalsEntityWithStatus;
    index: number;
    updateElement: (option: VitalsEntity, id: string) => void;
    units: UnitsWithRangesEntityAndId[] | undefined | null;
    defaultUnit: UnitsWithRangesEntityAndId | undefined | null;
    isLoadedFromIdb: boolean;
    containerId?: string;
    vitalConfig?: VitalsConfig;
    onChange: (option: VitalsEntity, inp: string) => void;
    lockStatus?: boolean;
}> = ({
    el,
    updateElement,
    units,
    defaultUnit,
    isLoadedFromIdb,
    containerId,
    vitalConfig,
    onChange,
    lockStatus,
}) => {
    const [selectedUnit, setSelectedUnit] = useState(defaultUnit);
    const [isEditMode, setEditMode] = useState(false);
    const [inputValue, setInputValue] = useState(el.value.qt || '');
    const [vitallockStatus, setLockStatus] = useState(lockStatus);
    const dispatch = useDispatch();

    const onUnitChange = (option?: UnitsWithRangesEntityAndId) => {
        const currentVitalId = vitalConfig?.selectedItems.find((i) => i.id === el.id)?.id;
        const newVitalsConfig = vitalConfig?.selectedItems.map((ele) =>
            currentVitalId === ele.id ? { ...ele, defaultOptionId: option?.code_id } : ele,
        );
        dispatch(
            upsertVitalsConfig({
                selectedItems: newVitalsConfig || [],
            }),
        );
        setSelectedUnit(option);

        if (el.value.qt) {
            updateElement(
                {
                    ...el,
                    value: {
                        ...el.value,
                        unit: option?.dis_unit || '',
                        code: option?.code,
                        code_id: option?.code_id,
                        safe: option?.safe,
                    },
                },
                el.id,
            );
        }
    };

    const onUnitTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedUnit({
            id: e.target.value,
            dis_unit: e.target.value,
            code: '',
            code_id: '',
            system: '',
            safe: { low: '', high: '', type: 'string' },
        });
        if (el.value.qt) {
            updateElement(
                {
                    ...el,
                    value: {
                        ...el.value,
                        unit: e.target.value,
                    },
                },
                el.id,
            );
        }
        setEditMode(false);
    };

    const toggleLockVital = () => {
        if (vitallockStatus === true || el.value.qt) {
            track('LOCK_VITALS', { locked: !vitallockStatus === true ? 'true' : 'false' });
            setLockStatus((prev) => {
                updateElement({ ...el, locked: !prev }, el.id);
                return !prev;
            });
        } else {
            track('LOCK_VITALS_WITHOUT_VALUE');
            notification.info({ message: 'Enter a value to apply lock', duration: 5 });
        }
    };

    useEffect(() => {
        setInputValue(el.value.qt);
    }, [el.value.qt]);

    useEffect(() => {
        setSelectedUnit(defaultUnit);
    }, [el.value.unit, el.value.qt]);

    const value = el.value?.qt || '';
    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inp = e.target.value;
        if (selectedUnit?.code_id === 'vu-3019850218') {
            if (/^\d?\d?['.]?\d?\d?$/.test(inp)) setInputValue(inp);
        } else {
            setInputValue(inp);
        }
        onChange(el, inp);
    };

    const lockIcon = (
        <span className="w-15 max-w-15 mr-10 cursor-pointer" onClick={toggleLockVital}>
            {vitallockStatus ? (
                <svg
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M4.63477 4.28125V5.5H8.69727V4.28125C8.69727 3.16406 7.7832 2.25 6.66602 2.25C5.52344 2.25 4.63477 3.16406 4.63477 4.28125ZM3.00977 5.5V4.28125C3.00977 2.27539 4.63477 0.625 6.66602 0.625C8.67188 0.625 10.3223 2.27539 10.3223 4.28125V5.5H10.7285C11.6172 5.5 12.3535 6.23633 12.3535 7.125V12C12.3535 12.9141 11.6172 13.625 10.7285 13.625H2.60352C1.68945 13.625 0.978516 12.9141 0.978516 12V7.125C0.978516 6.23633 1.68945 5.5 2.60352 5.5H3.00977Z"
                        fill="#215FFF"
                    />
                </svg>
            ) : (
                <svg
                    width="15"
                    height="14"
                    viewBox="0 0 15 14"
                    fill="none"
                    className="unlocked-vitals"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M9.29102 4.28125V5.5H10.1035C10.9922 5.5 11.7285 6.23633 11.7285 7.125V12C11.7285 12.9141 10.9922 13.625 10.1035 13.625H1.97852C1.06445 13.625 0.353516 12.9141 0.353516 12V7.125C0.353516 6.23633 1.06445 5.5 1.97852 5.5H7.66602V4.28125C7.66602 2.27539 9.29102 0.625 11.3223 0.625C13.3281 0.625 14.9785 2.27539 14.9785 4.28125V5.5C14.9785 5.95703 14.5977 6.3125 14.166 6.3125C13.709 6.3125 13.3535 5.95703 13.3535 5.5V4.28125C13.3535 3.16406 12.4395 2.25 11.3223 2.25C10.1797 2.25 9.29102 3.16406 9.29102 4.28125Z"
                        fill="#767676"
                    />
                </svg>
            )}
        </span>
    );

    const isDateVital = VITALS_AS_DATE.includes(el.id);

    return (
        <div
            className={`flex vital-cell pl-8 hover:bg-darwin-neutral-50 items-center rounded-8 flex-wrap lg:space-y-0 space-y-8 items-start w-full`}
        >
            <div
                className={`flex flex-1 frequently-item-mh items-center  ${
                    el.status ? 'active ' : ''
                } `}
            >
                <div className="flex items-center group vital-section">
                    <div className="flex items-center justify-center min-w-32 min-h-32 w-32 h-32 text-12 rounded-full bg-darwin-neutral-50 toggler">
                        {vitalToIconMapping(el?.id)}
                    </div>
                    <p className="ml-8 word-break-word text-14 min-w-100">
                        {el?.dis_name || el?.name}
                    </p>
                </div>
            </div>

            <div className={`flex justify-between items-center gap-12`}>
                <div
                    className={`vital-container flex items-center ml-16 space-x-8 overflow-hidden w-140 ${
                        isLoadedFromIdb
                            ? el.value.qt
                                ? 'filled-vitals-cell-input-container border-darwin-neutral-1000 rounded-2'
                                : 'vitals-cell-input-container'
                            : ''
                    }`}
                    style={{ fontWeight: 100, minHeight: 48 }}
                >
                    {isDateVital ? (
                        <DatePicker
                            picker="date"
                            value={inputValue ? moment(inputValue, "DD MMM'YY") : null}
                            onChange={(t) => {
                                if (t) {
                                    const formatted = t.format("DD MMM'YY");
                                    onChangeHandler({
                                        target: { value: formatted },
                                    } as ChangeEvent<HTMLInputElement>);
                                    updateElement(
                                        {
                                            ...el,
                                            value: {
                                                ...el.value,
                                                qt: formatted,
                                                unit: selectedUnit?.dis_unit || el.value.unit,
                                                code: selectedUnit?.code || el.value.code,
                                                code_id: selectedUnit?.code_id || el.value.code_id,
                                                safe: selectedUnit?.safe,
                                            },
                                        },
                                        el.id,
                                    );
                                } else {
                                    onChangeHandler({
                                        target: { value: '' },
                                    } as ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            allowClear={false}
                            format="DD MMM'YY"
                            disabledDate={(date) => date.isAfter(moment(), 'day')}
                        />
                    ) : (
                        <Input
                            type={selectedUnit?.safe?.type === 'Numeric' ? 'number' : 'text'}
                            defaultValue={value}
                            allowClear
                            onChange={onChangeHandler}
                            onPressEnter={(e) => {
                                e.currentTarget.blur();
                            }}
                            value={inputValue}
                            className={`${el.value.qt ? 'border border-darwin-neutral-1000' : ''}`}
                            onBlur={(e) => {
                                value !== e.target.value &&
                                    updateElement(
                                        {
                                            ...el,
                                            value: {
                                                ...el.value,
                                                qt: e.target.value,
                                                unit: selectedUnit?.dis_unit || el.value.unit,
                                                code: selectedUnit?.code || el.value.code,
                                                code_id: selectedUnit?.code_id || el.value.code_id,
                                                safe: selectedUnit?.safe,
                                            },
                                        },
                                        el.id,
                                    );
                            }}
                            addonAfter={
                                isLoadedFromIdb && (
                                    <Fragment>
                                        {units && units.length > 0 && !isEditMode ? (
                                            <AntSelect
                                                tabIndex={-1}
                                                containerIdToAttachDropdown={containerId}
                                                className="bg-transparent"
                                                showArrow={false}
                                                allowClear={false}
                                                placeholder={'unit'}
                                                optionFilterProp="children"
                                                options={units}
                                                customDropdownWidth={80}
                                                setValue={onUnitChange}
                                                filterOption={true}
                                                value={selectedUnit || undefined}
                                                getValue={(o) => o.dis_unit}
                                                getLabelToCompare={(o) => o.dis_unit}
                                                style={{ opacity: 0.6, fontSize: 10 }}
                                            />
                                        ) : (
                                            <Input
                                                defaultValue={el.value.unit || ''}
                                                placeholder="Unit"
                                                onPressEnter={(e) => {
                                                    e.currentTarget.blur();
                                                }}
                                                style={{
                                                    width: '60px',
                                                    paddingLeft: 0,
                                                    paddingRight: 0,
                                                    paddingBottom: 0,
                                                    fontSize: 10,
                                                }}
                                                onBlur={onUnitTextChange}
                                            />
                                        )}
                                    </Fragment>
                                )
                            }
                        />
                    )}

                    {isLocalItem(el.id) && !isEditMode && (
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 13 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="cursor-pointer edit-container"
                            onClick={() => setEditMode(true)}
                        >
                            <path
                                d="M12.1484 2.25781L10.7422 0.851562C10.3203 0.40625 9.59375 0.40625 9.14844 0.851562L7.83594 2.16406L10.8125 5.16406L12.1484 3.85156C12.5938 3.40625 12.5938 2.67969 12.1484 2.25781ZM1.22656 8.77344C1.15625 8.82031 1.13281 8.89062 1.10938 8.96094L0.5 12.0312C0.453125 12.2891 0.640625 12.5 0.851562 12.5C0.875 12.5 0.921875 12.5 0.945312 12.5L4.01562 11.8906C4.08594 11.8672 4.15625 11.8438 4.22656 11.7734L10.2969 5.70312L7.29688 2.70312L1.22656 8.77344ZM3.57031 10.8359L1.8125 11.1875L2.16406 9.42969L2.75 8.84375V10.25H4.15625L3.57031 10.8359Z"
                                fill="#3E445C"
                            />
                        </svg>
                    )}
                </div>
                <AntTooltip
                    placement="top"
                    trigger="hover"
                    content={
                        vitallockStatus
                            ? 'This value will repeat in future visits'
                            : 'Repeat this value in future visits'
                    }
                >
                    {lockIcon}
                </AntTooltip>
            </div>
        </div>
    );
};

export const VitalTableComponent = connect(
    (state: GlobalState, props: { vitalConfigData?: VitalsConfig }) => ({
        vitalConfigData: props.vitalConfigData || getVitalsPadConfig(state),
    }),
)(VitalTable);
